import { useEffect } from 'react';
import { ActionFooter, NewCheckbox, DatePickerInputFormik, SearchableDropdownMulti, SearchableDropdown, Dropdown } from '../../Common';
import { Formik, FormikHelpers } from 'formik';
import { useSelector } from 'react-redux';
import { selectOrganizationMembers } from '../../../store/slices/add-edit-policy.slice';
import {
    useHttpGetEvaluationRolesOnMount,
    useHttpGetMembersOMX,
    useHttpGetTopicsByCategoryNameOnMount,
    useHttpRequestEvaluation,
    useOrganizationId,
} from '../../../hooks';
import { RequestEvaluationDto } from '../../../commonTypes/user.types';
import SetCard from '../SetCard';

interface FormValues {
    evaluator: string;
    evaluatees: string[];
    role: string;
    selectedTopicIds: string[];
    isAnonymous: boolean;
    isNoteRequired: boolean;
    dueDate: string;
}

const CreateEvaluationForm = () => {
    const orgMembers = useSelector(selectOrganizationMembers);
    const organizationId = useOrganizationId();
    const { exec: fetchMembers } = useHttpGetMembersOMX(organizationId);
    const { exec: requestEvaluation } = useHttpRequestEvaluation();

    const { data: developerTopics, isReady: developerTopicsReady } = useHttpGetTopicsByCategoryNameOnMount('Developer');
    const { data: devopsTopics, isReady: devopsTopicsReady } = useHttpGetTopicsByCategoryNameOnMount('DevOps');
    const { data: culturalTopics, isReady: culturalTopicsReady } = useHttpGetTopicsByCategoryNameOnMount('Cultural');

    const { isReady: rolesReady, data: evaluationRoles } = useHttpGetEvaluationRolesOnMount();

    const formatDirectionLabel = (direction: string) => {
        return direction
            .split('__')
            .map(part =>
                part
                    .split('_')
                    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
                    .join(' ')
            )
            .join(' to ');
    };

    const evaluatorOptions = rolesReady
        ? evaluationRoles.map(role => ({
              value: role.id,
              label: formatDirectionLabel(role.direction),
          }))
        : [];

    const initialValues: FormValues = {
        evaluator: '',
        evaluatees: [],
        role: '',
        selectedTopicIds: [],
        isAnonymous: false,
        isNoteRequired: false,
        dueDate: '',
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
        const requestEvaluationDto: RequestEvaluationDto = {
            evaluatorUserSub: values.evaluator,
            evaluateeUserSubs: values.evaluatees,
            evaluatorToEvaluateeRoleId: values.role,
            topicIds: values.selectedTopicIds,
            isAnonymous: values.isAnonymous,
            isNoteRequired: values.isNoteRequired,
            dueDate: values.dueDate,
        };

        requestEvaluation(requestEvaluationDto);
        formikHelpers.setSubmitting(false);
    };

    const handleCancel = () => {
        window.history.back();
    };

    const userMenuItems = orgMembers.map(member => ({
        label: `${member.givenName} ${member.familyName}`,
        value: member.userSub,
    }));

    const topicsReady = developerTopicsReady && devopsTopicsReady && culturalTopicsReady;

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
            {formik => {
                const { values, setFieldValue, handleSubmit, isSubmitting } = formik;
                return (
                    <>
                        <form onSubmit={handleSubmit} className="create-evaluation-form">
                            <h2 className="create-evaluation-form__title">Add New Evaluation</h2>

                            <div className="create-evaluation-form__section">
                                <h3 className="create-evaluation-form__section-title">Evaluator & Evaluatee Informations</h3>
                                <div className="create-evaluation-form__dropdowns-container">
                                    <div className="create-evaluation-form__dropdown-wrapper">
                                        <SearchableDropdown
                                            label="Select Evaluator"
                                            menuItems={userMenuItems}
                                            selected={userMenuItems.find(item => item.value === values.evaluator) || { label: '', value: '' }}
                                            onChange={item => setFieldValue('evaluator', item.value)}
                                            placeholder="Please Choose"
                                        />
                                    </div>
                                    <div className="create-evaluation-form__dropdown-wrapper">
                                        <SearchableDropdownMulti
                                            label="Select Evaluatees"
                                            menuItems={userMenuItems}
                                            selectedValues={values.evaluatees}
                                            onChange={selectedValues => setFieldValue('evaluatees', selectedValues)}
                                            placeholder="Please Choose"
                                        />
                                    </div>
                                    <div className="create-evaluation-form__dropdown-wrapper create-evaluation-form__dropdown-wrapper--role">
                                        <Dropdown
                                            label="Select Role"
                                            menuItems={evaluatorOptions}
                                            selected={evaluatorOptions.find(item => item.value === values.role) || { label: '', value: '' }}
                                            onChange={item => setFieldValue('role', item.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="create-evaluation-form__section">
                                <div className="create-evaluation-form__sets-grid">
                                    {topicsReady && (
                                        <>
                                            <div className="create-evaluation-form__technical-topics">
                                                <h3 className="create-evaluation-form__topic-header">Technical Topics</h3>
                                                <div className="create-evaluation-form__technical-sets">
                                                    <SetCard
                                                        id="developer-set"
                                                        title="Developer Set"
                                                        topics={developerTopics}
                                                        selectedTopicIds={values.selectedTopicIds}
                                                        onChange={topicIds => setFieldValue('selectedTopicIds', topicIds)}
                                                    />
                                                    <SetCard
                                                        id="devops-set"
                                                        title="DevOps Set"
                                                        topics={devopsTopics}
                                                        selectedTopicIds={values.selectedTopicIds}
                                                        onChange={topicIds => setFieldValue('selectedTopicIds', topicIds)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="create-evaluation-form__cultural-topics">
                                                <h3 className="create-evaluation-form__topic-header">Cultural Topics</h3>
                                                <SetCard
                                                    id="cultural-set"
                                                    title="Cultural Set"
                                                    topics={culturalTopics}
                                                    selectedTopicIds={values.selectedTopicIds}
                                                    onChange={topicIds => setFieldValue('selectedTopicIds', topicIds)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="create-evaluation-form__section">
                                <h3 className="create-evaluation-form__section-title">Other Information</h3>
                                <div className="create-evaluation-form__other-info">
                                    <div className="create-evaluation-form__field">
                                        <DatePickerInputFormik
                                            className="invoice-create__field"
                                            context={formik}
                                            id="dueDate"
                                            label="Due Date"
                                            placeholder="Please Select"
                                            date={values.dueDate ? new Date(values.dueDate) : undefined}
                                            changeDate={date => {
                                                if (date) {
                                                    setFieldValue('dueDate', date.toFormat('yyyy-LL-dd'));
                                                }
                                            }}
                                            onPeriodChange={() => {}}
                                            openAbove={true}
                                            enablePastDays={false}
                                        />
                                    </div>

                                    <div className="create-evaluation-form__field">
                                        <NewCheckbox
                                            id="isAnonymous"
                                            label="Should the evaluation be anonymous?"
                                            checked={values.isAnonymous}
                                            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('isAnonymous', e.target.checked)}
                                        />
                                    </div>

                                    <div className="create-evaluation-form__field">
                                        <NewCheckbox
                                            id="isNoteRequired"
                                            label="Are the notes required?"
                                            checked={values.isNoteRequired}
                                            handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setFieldValue('isNoteRequired', e.target.checked)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                        <ActionFooter
                            buttons={[
                                {
                                    text: 'Cancel',
                                    variant: 'text',
                                    color: 'blue',
                                    onClick: handleCancel,
                                    disabled: isSubmitting,
                                },
                                {
                                    text: 'Add New Evaluation',
                                    variant: 'contained',
                                    onClick: () => handleSubmit(),
                                    disabled: isSubmitting,
                                },
                            ]}
                            className="create-evaluation-form__footer"
                        />
                    </>
                );
            }}
        </Formik>
    );
};

export default CreateEvaluationForm;
